import { sanitizeFilterQueryValue } from '@/common/utils/sanitizeFilterQueryValue';

/**
 * Keeps every non-DRAFT status, not just the assignable ones: historical
 * transactions belong to INACTIVE and ONBOARDING members, so the filter has to
 * keep offering them.
 */
export const AGENTS_BASE_QUERY = 'roles[in]=[AGENT] & status[nin]=[DRAFT]';

const TEAMS_BASE_QUERY = 'status[nin]=[DRAFT]';

/**
 * Only fields that appear in an option's label (`"First Last - TeamAlias"`), so
 * a server-side match is always visible to the user and the dropdown's local
 * filter never hides a row the server returned. `name` is the concatenation of
 * first and last name, so it subsumes both.
 */
const SEARCH_FIELDS = ['name', 'team.alias'] as const;

/**
 * Builds the name-match OR group, or `undefined` when the input can't produce a
 * well-formed one.
 *
 * Returning `undefined` rather than an empty clause is load-bearing: a clause
 * that fails the parser's `field[operator]=value` shape does not error, it
 * silently falls back to an open-text search across fields like `email` and
 * `phone`. Callers treat `undefined` as "don't fetch".
 */
export function buildAgentNameMatchClause(
  searchValue: string,
): string | undefined {
  const value = sanitizeFilterQueryValue(searchValue);
  if (!value) return undefined;

  return `(${SEARCH_FIELDS.map((field) => `${field}[match]=${value}`).join(' | ')})`;
}

/**
 * The two ways a broker reaches members without an explicit grant: supervising
 * the member's team, or managing the brokerage that team belongs to.
 *
 * Both fields are UUIDs in the DSL (`eq`/`in` only, no `match`), and a
 * non-UUID value is rejected with a 400 rather than silently ignored.
 */
export function buildBrokerAnchorClause(userId: string): string {
  return `(supervisingBroker.userId[eq]=${userId} | brokerage.managingBroker.userId[eq]=${userId})`;
}

interface BrokerQueryParams {
  userId: string;
  /**
   * BROKER ∧ ADMIN callers get the org-wide default view, so the anchor
   * predicates are dropped and platform's own org scoping bounds the result.
   */
  isAdmin: boolean;
}

export function buildBrokerAgentsQuery({
  userId,
  isAdmin,
  searchValue,
}: BrokerQueryParams & { searchValue?: string }): string {
  const nameClause = searchValue
    ? buildAgentNameMatchClause(searchValue)
    : undefined;

  const clauses = [AGENTS_BASE_QUERY];
  if (nameClause) clauses.push(nameClause);
  if (!isAdmin) clauses.push(buildBrokerAnchorClause(userId));

  return clauses.join(' & ');
}

export function buildBrokerTeamsQuery({
  userId,
  isAdmin,
}: BrokerQueryParams): string {
  const clauses = [TEAMS_BASE_QUERY];
  if (!isAdmin) clauses.push(buildBrokerAnchorClause(userId));

  return clauses.join(' & ');
}
